import { useQueryClient } from '@tanstack/react-query';
import {
  CancelDebtRepaymentPlanRequest,
  DebtRepaymentPlanRequest,
  ReceivablesLocationDetail,
  ResolveOutstandingItemRequest,
  cancelDebtRepaymentPlan,
  createDebtRepaymentPlan,
  markOutstandingItemPaid,
  queryKeys,
  retryPayment,
  updateDebtRepaymentPlan,
  useBusyAction,
  writeOffOutstandingItem,
} from '@vgrubs/components';

/**
 * The manual side of receivables: close an item as paid or written off,
 * retry the bank payment behind one, and start, change or stop a debt
 * repayment plan.
 *
 * The item routes answer with the location's whole detail, which goes straight
 * into its cache; the list is invalidated alongside because its totals, aging
 * and order move with every one of them.
 *
 * Each resolves to whether it went through, so a dialog can close on success
 * and keep what was typed (a wrong password is the usual failure) otherwise.
 */
export const useReceivablesActions = () => {
  const queryClient = useQueryClient();
  const { busy, run } = useBusyAction();

  const apply = (locationId: number, detail: ReceivablesLocationDetail) => {
    queryClient.setQueryData(
      queryKeys.admin.receivables.location(locationId),
      detail
    );
    queryClient.invalidateQueries({
      queryKey: queryKeys.admin.receivables.list,
    });
  };

  return {
    busy,
    markPaid: (
      locationId: number,
      itemId: number,
      data: ResolveOutstandingItemRequest
    ) =>
      run(async () => {
        const { data: res } = await markOutstandingItemPaid(itemId, data);
        apply(locationId, res.data);
      }, 'Charge marked as paid.'),
    writeOff: (
      locationId: number,
      itemId: number,
      data: ResolveOutstandingItemRequest
    ) =>
      run(async () => {
        const { data: res } = await writeOffOutstandingItem(itemId, data);
        apply(locationId, res.data);
      }, 'Charge written off.'),
    /**
     * The client payments retry, reached from the item it failed for. The
     * backend refuses it for vDrive balance bank payments; its message says
     * so. Nothing comes back but the payment, so the detail is refetched.
     */
    retryBankPayment: (locationId: number, clientPaymentId: number) =>
      run(async () => {
        await retryPayment(clientPaymentId);
        queryClient.invalidateQueries({
          queryKey: queryKeys.admin.receivables.location(locationId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.admin.receivables.list,
        });
      }, 'Bank payment retried.'),
    createPlan: (locationId: number, data: DebtRepaymentPlanRequest) =>
      run(async () => {
        const { data: res } = await createDebtRepaymentPlan(locationId, data);
        apply(locationId, res.data);
      }, 'Repayment plan started.'),
    updatePlan: (
      locationId: number,
      planId: number,
      data: DebtRepaymentPlanRequest
    ) =>
      run(async () => {
        const { data: res } = await updateDebtRepaymentPlan(planId, data);
        apply(locationId, res.data);
      }, 'Repayment plan saved.'),
    cancelPlan: (
      locationId: number,
      planId: number,
      data: CancelDebtRepaymentPlanRequest
    ) =>
      run(async () => {
        const { data: res } = await cancelDebtRepaymentPlan(planId, data);
        apply(locationId, res.data);
      }, 'Repayment plan stopped.'),
  };
};
